import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getEntities } from '@/features/evault-details/data/entities'
import { selectGovernorsFromAddresses } from '@/features/evault-details/logic/selectGovernors'
import type { EVaultData } from '@/features/vaults/logic/useEVaults'
import type { Address } from 'viem'
import type { EVaultSourceData, ExposureItem, YieldExposure, YieldOpportunity } from '../types'
import { makeYieldOpportunityId } from './makeYieldOpportunityId'

function createTokenFromVault(vault: EVaultData): Token | undefined {
  const tokenInfo = vault.underlyingTokenInfo
  if (!tokenInfo) return undefined

  const unitPriceUsd =
    vault.tvlUsd && vault.totalAssets?.gt(0) ? vault.tvlUsd.dividedBy(vault.totalAssets).toString() : '0'

  return new Token({
    symbol: TokenSymbol(tokenInfo.symbol),
    name: tokenInfo.name,
    decimals: tokenInfo.decimals,
    address: CheckedAddress(tokenInfo.address),
    unitPriceUsd,
    logoURI: tokenInfo.logoURI,
  })
}

function getEVaultCuratorName(vault: EVaultData): string | undefined {
  if (!vault.owner) {
    return undefined
  }

  const entities = getEntities()
  const governorData = selectGovernorsFromAddresses(vault.owner, entities)

  if (governorData.governors[0]?.name) {
    return governorData.governors[0].name
  }

  // Vault has an owner but not in our entities - show as managed
  return 'Managed Vault'
}

function getEVaultCuratorLogo(vault: EVaultData): string | undefined {
  if (!vault.owner) {
    return undefined
  }

  const entities = getEntities()
  const governorData = selectGovernorsFromAddresses(vault.owner, entities)

  if (governorData.governors[0]?.logo) {
    return governorData.governors[0].logo
  }

  return undefined
}

export interface StrategyUtilizationMap {
  get(address: string): number | undefined
}

function mapStrategiesToExposure(vault: EVaultData): YieldExposure | null | undefined {
  // Data not available (on-chain fallback sets strategies to undefined)
  if (vault.strategies === undefined) {
    return null
  }

  // Genuinely no strategies
  if (vault.strategies.length === 0) {
    return undefined
  }

  const activeStrategies = vault.strategies.filter((s) => s.status === 'active')
  if (activeStrategies.length === 0) {
    return undefined
  }

  const strategies: ExposureItem[] = activeStrategies.map((strategy) => ({
    collateralAddress: strategy.strategy as Address,
    assetAddress: strategy.strategyAsset as Address | null,
    name: strategy.strategyVaultName || 'Strategy Vault',
    allocationUsd: strategy.allocatedAssetsUSD,
  }))

  return {
    type: 'vault-strategies',
    strategies,
  }
}

function calculateWeightedUtilization(
  vault: EVaultData,
  strategyUtilizationMap?: StrategyUtilizationMap,
): number | undefined {
  if (!vault.strategies || vault.strategies.length === 0 || !strategyUtilizationMap) {
    return undefined
  }

  const activeStrategies = vault.strategies.filter((s) => s.status === 'active')
  if (activeStrategies.length === 0) {
    return undefined
  }

  let totalWeightedUtilization = 0
  let totalAllocation = 0

  for (const strategy of activeStrategies) {
    const utilization = strategyUtilizationMap.get(strategy.strategy.toLowerCase())
    const allocation = strategy.allocatedAssetsUSD || 0

    if (utilization !== undefined && allocation > 0) {
      totalWeightedUtilization += utilization * allocation
      totalAllocation += allocation
    }
  }

  if (totalAllocation === 0) {
    return undefined
  }

  return totalWeightedUtilization / totalAllocation
}

export function mapEVaultToYieldOpportunity(
  vault: EVaultData,
  strategyUtilizationMap?: StrategyUtilizationMap,
): YieldOpportunity {
  const chainId = E_EARN_VAULT_CHAIN_ID
  const assetSymbol = vault.assetSymbol || vault.symbol

  const hasPosition = vault.userBalance !== undefined && Number(vault.userBalance) > 0

  let userDepositUsd: NormalizedUnitNumber | undefined
  if (hasPosition && vault.totalAssets?.gt(0) && vault.totalSupply?.gt(0) && vault.tvlUsd?.gt(0)) {
    const depositedAssets = vault.userBalance!.multipliedBy(vault.totalAssets).dividedBy(vault.totalSupply)
    const assetPriceUsd = vault.tvlUsd.dividedBy(vault.totalAssets)
    userDepositUsd = NormalizedUnitNumber(depositedAssets.multipliedBy(assetPriceUsd))
  }

  const sourceData: EVaultSourceData = {
    type: 'evault',
    totalAssets: vault.totalAssets,
  }

  return {
    id: makeYieldOpportunityId('evault', chainId, vault.address),
    source: 'evault',
    chainId,
    address: vault.address,
    name: vault.name,
    symbol: vault.symbol,
    assetSymbol,
    tokenInfo: vault.underlyingTokenInfo,
    token: vault.token ?? createTokenFromVault(vault),
    supplyAPY: vault.supplyAPY ?? 0,
    supplyApy30d: vault.supplyApy30d,
    tvlUsd: vault.tvlUsd,
    walletBalance: vault.walletBalance,
    walletBreakdown: vault.walletBreakdown,
    userDeposit: vault.userBalance,
    userDepositUsd,
    hasPosition,
    curatorName: getEVaultCuratorName(vault),
    curatorLogo: getEVaultCuratorLogo(vault),
    market: undefined,
    points: undefined,
    exposure: mapStrategiesToExposure(vault),
    utilization: calculateWeightedUtilization(vault, strategyUtilizationMap),
    sourceData,
  }
}

export function mapEVaultsToYieldOpportunities(
  vaults: EVaultData[],
  strategyUtilizationMap?: StrategyUtilizationMap,
): YieldOpportunity[] {
  return vaults.map((vault) => mapEVaultToYieldOpportunity(vault, strategyUtilizationMap))
}
